// @ts-ignore - three types not installed
import * as THREE from 'three';
// @ts-ignore - three types not installed
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import {
  GeoJSONPolygon,
  GeoJSONMultiPolygon,
  GeneratedUnit,
  BuildingGenerationParams,
} from '@/types/osm';
import { NYCCondoUnit } from '@/types/nyc';
import { Unit } from '@urbanmix-tech/shared-js';
import { uploadGlbFile } from '@/data';

const DEFAULT_FLOOR_HEIGHT = 3; // meters
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * Extract floor number from NYC unit name (e.g., "1A" -> 1, "PHA" -> "PH")
 * @param unitName - NYC unit name
 * @returns Floor number or special floor designation
 */
function extractFloorFromUnitName(unitName: string): number {
  // Handle penthouse units (PHA, PHB, etc.)
  if (unitName.startsWith('PH')) {
    return 999; // Use high number to sort penthouses to top
  }

  // Extract numeric prefix (e.g., "1A" -> 1, "12B" -> 12)
  const match = unitName.match(/^(\d+)/);
  if (match) {
    return parseInt(match[1]);
  }

  return 1; // Default to floor 1 if no number found
}

/**
 * Extract line letter from NYC unit name (e.g., "1A" -> "A", "PHA" -> "A")
 * @param unitName - NYC unit name
 * @returns Line letter
 */
function extractLineFromUnitName(unitName: string): string {
  // Handle penthouse units (PHA, PHB, etc.)
  if (unitName.startsWith('PH')) {
    return unitName.substring(2) || 'A';
  }

  // Extract letter suffix (e.g., "1A" -> "A", "12BC" -> "BC")
  const match = unitName.match(/[A-Z]+$/);
  if (match) {
    return match[0];
  }

  return 'A'; // Default to A if no letter found
}

/**
 * Generate unit layout from NYC Property Portal data
 * @param nycUnits - Array of NYC condo units
 * @param footprintArea - Footprint area in square meters (optional)
 * @returns Array of generated units
 */
export function generateUnitLayoutFromNYC(
  nycUnits: NYCCondoUnit[],
  footprintArea?: number
): GeneratedUnit[] {
  const units: GeneratedUnit[] = [];

  // Default bedroom/bathroom estimates
  const defaultBedrooms = 2;
  const defaultBathrooms = 1;

  // Convert NYC units to GeneratedUnit format
  for (const nycUnit of nycUnits) {
    const unitName = nycUnit.unit;
    const floor = extractFloorFromUnitName(unitName);
    const line = extractLineFromUnitName(unitName);

    units.push({
      apartmentId: unitName,
      meshId: `apt_${unitName}`,
      floor,
      line,
      numBedrooms: defaultBedrooms,
      numBathrooms: defaultBathrooms,
      estimatedArea: footprintArea ? footprintArea / nycUnits.length : undefined,
    });
  }

  return units;
}

/**
 * Generate unit layout from building parameters
 * @param floors - Number of floors
 * @param totalUnits - Total number of units
 * @param footprintArea - Footprint area in square meters (optional)
 * @returns Array of generated units
 */
export function generateUnitLayout(
  floors: number,
  totalUnits: number,
  footprintArea?: number
): GeneratedUnit[] {
  const units: GeneratedUnit[] = [];

  // Calculate units per floor
  const unitsPerFloor = Math.ceil(totalUnits / floors);

  // Estimate bedrooms based on footprint area if available
  const estimateBedrooms = (area?: number): number => {
    if (!area) return 2; // Default 2BR
    const areaPerUnit = area / unitsPerFloor;
    if (areaPerUnit < 60) return 1;
    if (areaPerUnit < 90) return 2;
    if (areaPerUnit < 120) return 3;
    return 4;
  };

  const defaultBedrooms = estimateBedrooms(footprintArea);
  const defaultBathrooms = Math.min(defaultBedrooms, 2);

  let unitCount = 0;

  for (let floor = 1; floor <= floors && unitCount < totalUnits; floor++) {
    const unitsThisFloor = Math.min(unitsPerFloor, totalUnits - unitCount);

    for (let lineIndex = 0; lineIndex < unitsThisFloor; lineIndex++) {
      const line = LETTERS[lineIndex];
      const apartmentId = `${floor}${line}`;
      const meshId = `apt_${apartmentId}`;

      units.push({
        apartmentId,
        meshId,
        floor,
        line,
        numBedrooms: defaultBedrooms,
        numBathrooms: defaultBathrooms,
        estimatedArea: footprintArea ? footprintArea / unitsPerFloor : undefined,
      });

      unitCount++;
    }
  }

  return units;
}

/**
 * Calculate area of a polygon in square meters (approximate)
 * @param coordinates - Polygon coordinates [[lon, lat], ...]
 * @returns Area in square meters
 */
function calculatePolygonArea(coordinates: [number, number][]): number {
  if (coordinates.length < 3) return 0;

  // Convert to meters using approximate conversion
  // At equator: 1 degree ≈ 111km, adjusting for latitude
  const avgLat = coordinates.reduce((sum, [_, lat]) => sum + lat, 0) / coordinates.length;
  const latToMeters = 111000;
  const lonToMeters = 111000 * Math.cos((avgLat * Math.PI) / 180);

  // Convert coordinates to meters
  const metersCoords = coordinates.map(([lon, lat]) => [
    lon * lonToMeters,
    lat * latToMeters,
  ]);

  // Shoelace formula
  let area = 0;
  for (let i = 0; i < metersCoords.length - 1; i++) {
    const [x1, y1] = metersCoords[i];
    const [x2, y2] = metersCoords[i + 1];
    area += x1 * y2 - x2 * y1;
  }

  return Math.abs(area) / 2;
}

/**
 * Convert GeoJSON polygon coordinates to local coordinate system
 * @param coordinates - Array of [lon, lat] pairs
 * @param originLon - Origin longitude
 * @param originLat - Origin latitude
 * @param centerX - Center X to subtract
 * @param centerY - Center Y to subtract
 * @returns Array of local [x, y] coordinates
 */
function convertCoordinatesToLocal(
  coordinates: [number, number][],
  originLon: number,
  originLat: number,
  centerX: number,
  centerY: number
): [number, number][] {
  const latToMeters = 111000;
  const lonToMeters = 111000 * Math.cos((originLat * Math.PI) / 180);

  const localCoords: [number, number][] = [];
  for (let i = 0; i < coordinates.length - 1; i++) {
    const [lon, lat] = coordinates[i];
    const x = (lon - originLon) * lonToMeters - centerX;
    const y = (lat - originLat) * latToMeters - centerY;
    localCoords.push([x, y]);
  }
  return localCoords;
}

/**
 * Convert GeoJSON coordinates to Three.js Shape (supports holes/courtyards)
 * @param coordinates - GeoJSON polygon coordinates (array of rings)
 * @returns Three.js Shape
 */
function geoJSONToShape(coordinates: [number, number][][]): THREE.Shape {
  const shape = new THREE.Shape();

  if (coordinates.length === 0 || coordinates[0].length === 0) {
    return shape;
  }

  // First ring is the outer boundary
  const outerRing = coordinates[0];
  const [originLon, originLat] = outerRing[0];

  // Convert outer ring to local coords and calculate centroid
  const latToMeters = 111000;
  const lonToMeters = 111000 * Math.cos((originLat * Math.PI) / 180);

  let tempCoords: [number, number][] = [];
  for (let i = 0; i < outerRing.length - 1; i++) {
    const [lon, lat] = outerRing[i];
    const x = (lon - originLon) * lonToMeters;
    const y = (lat - originLat) * latToMeters;
    tempCoords.push([x, y]);
  }

  // Calculate centroid from outer ring
  let centerX = 0;
  let centerY = 0;
  for (const [x, y] of tempCoords) {
    centerX += x;
    centerY += y;
  }
  centerX /= tempCoords.length;
  centerY /= tempCoords.length;

  // Create outer shape
  const outerCoords = convertCoordinatesToLocal(outerRing, originLon, originLat, centerX, centerY);
  if (outerCoords.length > 0) {
    shape.moveTo(outerCoords[0][0], outerCoords[0][1]);
    for (let i = 1; i < outerCoords.length; i++) {
      shape.lineTo(outerCoords[i][0], outerCoords[i][1]);
    }
    shape.closePath();
  }

  // Add holes (inner rings) if they exist
  for (let ringIndex = 1; ringIndex < coordinates.length; ringIndex++) {
    const innerRing = coordinates[ringIndex];
    const holeCoords = convertCoordinatesToLocal(innerRing, originLon, originLat, centerX, centerY);

    if (holeCoords.length > 0) {
      const holePath = new THREE.Path();
      holePath.moveTo(holeCoords[0][0], holeCoords[0][1]);
      for (let i = 1; i < holeCoords.length; i++) {
        holePath.lineTo(holeCoords[i][0], holeCoords[i][1]);
      }
      holePath.closePath();
      shape.holes.push(holePath);
    }
  }

  return shape;
}

/**
 * Create 3D building geometry from GeoJSON footprint
 * @param footprint - GeoJSON polygon or multipolygon
 * @param height - Building height in meters
 * @param floors - Number of floors
 * @param unitsPerFloor - Units per floor
 * @returns Three.js Object3D with building geometry
 */
export function createBuildingGeometry(
  footprint: GeoJSONPolygon | GeoJSONMultiPolygon,
  height: number,
  floors: number,
  unitsPerFloor: number
): THREE.Object3D {
  const buildingGroup = new THREE.Object3D();
  buildingGroup.name = 'building';

  // Get the polygon coordinates (handle both Polygon and MultiPolygon)
  // For Polygon: coordinates is [outer, hole1, hole2, ...]
  // For MultiPolygon: coordinates is [[outer, hole1, ...], [outer2, ...], ...]
  const polygonCoords: [number, number][][] =
    footprint.type === 'Polygon'
      ? footprint.coordinates as [number, number][][]
      : footprint.coordinates[0] as [number, number][][]; // Use first polygon if MultiPolygon

  // Create shape from footprint (now supports holes)
  const shape = geoJSONToShape(polygonCoords);

  // Calculate floor height
  const floorHeight = height / floors;
  const unitHeight = floorHeight * 0.95; // Slightly smaller to show visual separation

  // Create each unit as a separate mesh
  for (let floor = 1; floor <= floors; floor++) {
    for (let lineIndex = 0; lineIndex < unitsPerFloor; lineIndex++) {
      const line = LETTERS[lineIndex];
      const unitName = `apt_${floor}${line}`;

      // Create unit geometry
      const unitExtrudeSettings = {
        depth: unitHeight,
        bevelEnabled: false,
      };

      const unitGeometry = new THREE.ExtrudeGeometry(shape, unitExtrudeSettings);

      // Rotate to correct orientation (Three.js extrudes along Z, we want Y)
      unitGeometry.rotateX(Math.PI / 2);

      // Create material with color based on unit line (consistent color per line across floors)
      const hue = (lineIndex / Math.max(unitsPerFloor - 1, 1)) * 280; // Distribute colors across lines
      const color = new THREE.Color(`hsl(${hue}, 65%, 60%)`);
      const material = new THREE.MeshStandardMaterial({
        color,
        roughness: 0.7,
        metalness: 0.2,
      });

      const unitMesh = new THREE.Mesh(unitGeometry, material);
      unitMesh.name = unitName;

      // Position at floor level
      unitMesh.position.y = (floor - 1) * floorHeight;

      // Add user data to identify this unit
      unitMesh.userData.floor = floor;
      unitMesh.userData.line = line;
      unitMesh.userData.unitId = unitName;

      buildingGroup.add(unitMesh);
    }
  }

  return buildingGroup;
}

/**
 * Export Three.js scene to GLB format
 * @param scene - Three.js scene or object
 * @returns Promise resolving to GLB Blob
 */
export async function exportToGLB(scene: THREE.Object3D): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const exporter = new GLTFExporter();

    exporter.parse(
      scene,
      (result: any) => {
        // result is ArrayBuffer when binary option is true
        const blob = new Blob([result as ArrayBuffer], {
          type: 'model/gltf-binary',
        });
        resolve(blob);
      },
      (error: any) => {
        reject(error);
      },
      {
        binary: true, // Export as GLB (binary)
        onlyVisible: true,
      }
    );
  });
}

/**
 * Upload GLB file to Firebase Storage using shared-js uploadGlbFile
 * @param blob - GLB file blob
 * @param projectId - Project ID
 * @param buildingId - Building ID
 * @returns Download URL
 */
export async function uploadGLBToFirebase(
  blob: Blob,
  projectId: string,
  buildingId: string = '1'
): Promise<string> {
  // Convert Blob to File for uploadGlbFile
  const timestamp = Date.now();
  const file = new File([blob], `building_${timestamp}.glb`, {
    type: 'model/gltf-binary',
  });

  // Use the shared uploadGlbFile function which handles CORS correctly
  const downloadURL = await uploadGlbFile(file, projectId, buildingId);

  if (!downloadURL) {
    throw new Error('Failed to upload GLB file to Firebase Storage');
  }

  return downloadURL;
}

/**
 * Upload GLB file to Embedded Server directly using JWT authentication
 * @param blob - GLB file blob
 * @param modelId - Project ID to use as the model identifier
 * @param buildingName - Building name for the filename
 * @param jwt - JWT token from authenticateUrbanMixEmbedded
 * @param metadata - Optional metadata to store with the building
 * @returns Response with building info and storage details
 */
export async function uploadGLBToEmbeddedServer(
  blob: Blob,
  modelId: string,
  buildingName: string,
  jwt: string,
  metadata?: Record<string, any>
): Promise<{
  success: boolean;
  buildingName: string;
  fileId: string;
  storagePath: string;
  message: string;
}> {
  const embeddedEndpoint = import.meta.env.VITE_UM_EMBEDDED_CLIENT_END_POINT;

  if (!embeddedEndpoint) {
    throw new Error('VITE_UM_EMBEDDED_CLIENT_END_POINT environment variable is not configured');
  }

  // Convert blob to base64 string
  const arrayBuffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64Data = btoa(binary);

  // Build request body with base64-encoded file data
  const requestBody: any = {
    modelId,
    fileData: base64Data,
  };

  // Add metadata fields if provided
  if (metadata) {
    if (metadata.apartmentPrefix) {
      requestBody.apartmentPrefix = metadata.apartmentPrefix;
    }
    if (metadata.apartmentNameParser) {
      requestBody.apartmentNameParser = metadata.apartmentNameParser;
    }
  }

  // Debug logging
  console.log('=== Uploading to Embedded Server ===');
  console.log('modelId:', modelId);
  console.log('buildingName:', buildingName);
  console.log('file size:', blob.size);
  console.log('base64 length:', base64Data.length);
  console.log('endpoint:', `${embeddedEndpoint}/createBuilding`);

  try {
    const response = await fetch(`${embeddedEndpoint}/createBuilding`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Upload failed: ${errorText} (${response.status})`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(`Upload failed: ${result.message || 'Unknown error'}`);
    }

    console.log('Upload successful:', result);

    // The server returns {success, modelId, message}
    // We need to construct the response in the expected format
    // Note: The actual storage path will be in Firebase Storage under the client's folder
    // Format: gs://{bucket}/{client}/{timestamp}_{modelId}_building.glb
    // For now, we'll use the modelId as both fileId and construct a placeholder path
    return {
      success: result.success,
      buildingName: buildingName,
      fileId: result.modelId,
      storagePath: result.modelId, // The modelId is used to reference the model in the embedded system
      message: result.message,
    };
  } catch (error) {
    console.error('Error uploading to embedded server:', error);
    throw new Error(
      `Failed to upload building to embedded server: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Generate complete building 3D model and upload
 * @param params - Building generation parameters
 * @returns Object containing model URL and generated units
 */
export async function generateBuilding(
  params: BuildingGenerationParams
): Promise<{
  modelUrl: string;
  units: GeneratedUnit[];
  footprintArea?: number;
}> {
  const {
    footprint,
    floors,
    totalUnits,
    floorHeight = DEFAULT_FLOOR_HEIGHT,
  } = params;

  if (!footprint) {
    throw new Error('Building footprint is required');
  }

  // Calculate footprint area
  const coordinates =
    footprint.type === 'Polygon'
      ? footprint.coordinates[0]
      : footprint.coordinates[0][0];
  const footprintArea = calculatePolygonArea(coordinates);

  // Generate unit layout
  const units = generateUnitLayout(floors, totalUnits, footprintArea);
  const unitsPerFloor = Math.ceil(totalUnits / floors);

  // Calculate building height
  const buildingHeight = floors * floorHeight;

  // Create 3D geometry
  const buildingObject = createBuildingGeometry(
    footprint,
    buildingHeight,
    floors,
    unitsPerFloor
  );

  // Create scene for export
  const scene = new THREE.Scene();
  scene.add(buildingObject);

  // Add lighting (required for proper GLB export)
  // Note: AmbientLight is not fully supported in GLB export, but we'll keep it for compatibility
  // GLTFExporter will show a warning but continue
  const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
  scene.add(ambientLight);

  const directionalLight = new THREE.DirectionalLight(0xffffff, 0.5);
  directionalLight.position.set(10, 10, 10);
  // Add target as child to prevent GLTFExporter warning
  const lightTarget = new THREE.Object3D();
  lightTarget.position.set(0, 0, -1);
  directionalLight.add(lightTarget);
  directionalLight.target = lightTarget;
  scene.add(directionalLight);

  // Export to GLB
  const glbBlob = await exportToGLB(scene);

  // Generate temporary project ID if not provided
  const projectId = params.buildingName || `temp_${Date.now()}`;

  // Upload to Firebase (building ID will be '1' by default)
  const modelUrl = await uploadGLBToFirebase(glbBlob, projectId, '1');

  return {
    modelUrl,
    units,
    footprintArea,
  };
}

/**
 * Convert GeneratedUnit to full Unit type for database
 * @param generatedUnit - Generated unit info
 * @param projectId - Project ID
 * @param buildingId - Building ID
 * @param includeRentRoll - Whether to include default rent roll data (optional)
 * @returns Full Unit object
 */
export function convertToUnit(
  generatedUnit: GeneratedUnit,
  projectId: string,
  buildingId: number,
  includeRentRoll: boolean = false
): any {
  const baseUnit = {
    id: generatedUnit.apartmentId,
    apartmentId: generatedUnit.apartmentId,
    meshId: generatedUnit.meshId,
    projectId,
    buildingId,
    numBedrooms: generatedUnit.numBedrooms,
    numBathrooms: generatedUnit.numBathrooms,
    estimatedArea: generatedUnit.estimatedArea,
    currentState: [],
    actions: [],
  };

  // Add rent roll fields if requested
  if (includeRentRoll) {
    return {
      ...baseUnit,
      leaseStartDate: null,
      leaseEndDate: null,
      actualRent: null,
      residentDeposit: null,
      currentState: 'LEASE', // Default to LEASE lifecycle
      currentSubState: 'Publish', // Default to Publish substate
    };
  }

  return baseUnit;
}

/**
 * Generate rent roll CSV data from units
 * @param units - Array of generated units
 * @returns CSV string in Appfolio format
 */
export function generateRentRollCSV(units: GeneratedUnit[]): string {
  const headers = ['Unit', 'Lease From', 'Lease To', 'Rent', 'Deposit', 'Status'];
  const rows = units.map((unit) => [
    unit.apartmentId,
    '', // Empty lease from
    '', // Empty lease to
    '', // Empty rent
    '', // Empty deposit
    'Vacant', // Default status
  ]);

  return [headers, ...rows].map((row) => row.join(',')).join('\n');
}
